import { Condition, ConditionValue } from '../types/segments/segments.types';

const NO_VALUE_OPS = new Set([
  'exists',
  'not_exists',
  'is_empty',
  'is_not_empty',
  'is_valid',
  'is_invalid',
  'is_timestamp',
  'is_not_timestamp',
  'in_segment',
  'not_in_segment',
  'has_ever',
  'has_not',
  'has_performed',
  'has_not_performed',
  'has_been_sent',
  'has_not_been_sent',
  'has_been_opened',
  'has_not_been_opened',
  'has_been_clicked',
  'has_not_been_clicked',
]);

const isEmpty = (v: any): boolean => {
  if (v == null || v === '') return true;
  if (typeof v === 'object') {
    if (v.start !== undefined && v.end !== undefined)
      return isEmpty(v.start) || isEmpty(v.end);
    if (v.date !== undefined) return isEmpty(v.date);
    return Object.keys(v).length === 0;
  }
  return false;
};

export const operatorRequiresValue = (op: string): boolean =>
  !NO_VALUE_OPS.has(op);

const checkNested = (
  n: any,
  needProp: boolean
): { valid: boolean; message?: string } => {
  if (!n || typeof n !== 'object')
    return { valid: false, message: 'Value is required for this operator' };
  if (needProp && (n.property == null || String(n.property).trim() === ''))
    return { valid: false, message: 'Property name is required' };
  const op = n.operator;
  if (!op || String(op).trim() === '')
    return { valid: false, message: 'Operator is required' };
  if (op === 'has_property') {
    const r = checkNested(n.value, true);
    if (!r.valid) return r;
  } else if (op === 'at_least_one') {
    const t = n.arrayType;
    if (!t || String(t).trim() === '')
      return { valid: false, message: 'Array type is required' };
    const r = checkNested(n.value, t === 'propertyValue');
    if (!r.valid) return r;
  } else if (operatorRequiresValue(op) && isEmpty(n.value))
    return { valid: false, message: 'Value is required for this operator' };
  return { valid: true };
};

type FieldErr = { field?: string; operator?: string; value?: string };

export const validateCondition = (
  c: Condition
): { isValid: boolean; errors: string[]; fieldErrors: FieldErr } => {
  const errors: string[] = [];
  const fe: FieldErr = {};

  if (c.condition === 'group')
    return { isValid: true, errors: [], fieldErrors: {} };

  if (!c.field?.trim()) {
    const msg =
      c.condition === 'segment'
        ? 'Segment is required'
        : c.condition === 'event'
          ? 'Event attribute is required'
          : 'Field is required';
    errors.push(msg);
    fe.field = msg;
  }
  if (!c.operator?.trim()) {
    errors.push('Operator is required');
    fe.operator = 'Operator is required';
  }
  if (c.condition === 'segment')
    return { isValid: !errors.length, errors, fieldErrors: fe };

  const noValTypes = ['form', 'page', 'device', 'screen', 'opt-out'];
  if (c.condition === 'event' || noValTypes.includes(c.condition || '')) {
    if (c.operator && !operatorRequiresValue(c.operator)) {
      if (!c.field?.trim()) {
        const msg =
          c.condition === 'event'
            ? 'Event field is required'
            : `${c.condition} field is required`;
        errors.push(msg);
        fe.field = msg;
      }
      return { isValid: !errors.length, errors, fieldErrors: fe };
    }
  }

  if (c.operator === 'has_property') {
    const r = checkNested(c.value, true);
    if (!r.valid) {
      errors.push(r.message!);
      fe.value = r.message;
    }
  } else if (c.operator === 'at_least_one') {
    if (!c.arrayType?.trim()) {
      errors.push('Array type is required');
      fe.value = 'Array type is required';
    } else {
      const r = checkNested(c.value, c.arrayType === 'propertyValue');
      if (!r.valid) {
        errors.push(r.message!);
        fe.value = r.message;
      }
    }
  } else if (
    c.operator &&
    c.operator !== 'has_property' &&
    c.operator !== 'at_least_one' &&
    operatorRequiresValue(c.operator) &&
    isEmpty(c.value)
  ) {
    errors.push('Value is required for this operator');
    fe.value = 'Value is required for this operator';
  }

  if (c.operator === 'between' || c.operator === 'is_between') {
    const rv = c.value as { start?: any; end?: any };
    const msg = 'Both start and end values are required for range operators';
    if (
      !c.value ||
      typeof c.value !== 'object' ||
      isEmpty(rv?.start) ||
      isEmpty(rv?.end)
    ) {
      errors.push(msg);
      fe.value = msg;
    }
  }

  return { isValid: !errors.length, errors, fieldErrors: fe };
};

const validateGroup = (
  list: Condition[],
  path: string,
  prefix: string,
  out: Record<string, FieldErr>
): string[] => {
  const errs: string[] = [];
  if (!list.length) {
    errs.push(`${path}: Group must contain at least one condition`);
    return errs;
  }
  for (let i = 0; i < list.length; i++) {
    const c = list[i];
    const key = prefix ? `${prefix}-${i}` : String(i);
    if (c.condition === 'group') {
      const gv = c.value as ConditionValue;
      if (gv?.conditions)
        errs.push(
          ...validateGroup(gv.conditions, `${path} > group ${i + 1}`, key, out)
        );
      else errs.push(`${path} > group ${i + 1}: Invalid group structure`);
    } else {
      const v = validateCondition(c);
      if (!v.isValid) {
        v.errors.forEach((e) =>
          errs.push(`${path} > condition ${i + 1}: ${e}`)
        );
        out[key] = v.fieldErrors;
      }
    }
  }
  return errs;
};

export interface FieldValidationErrors {
  [logicType: string]: {
    [conditionIndex: string]: FieldErr;
  };
}

export const validateConditions = (conditions: {
  [key: string]: Condition[];
}): {
  isValid: boolean;
  errors: string[];
  fieldErrors: FieldValidationErrors;
} => {
  const allErrors: string[] = [];
  const fieldErrors: FieldValidationErrors = {};
  let anyValid = false;
  for (const [logicType, arr] of Object.entries(conditions)) {
    if (!arr.length) continue;
    fieldErrors[logicType] = {};
    for (let i = 0; i < arr.length; i++) {
      const c = arr[i];
      if (c.condition === 'group') {
        const gv = c.value as ConditionValue;
        if (gv?.conditions) {
          const ge = validateGroup(
            gv.conditions,
            `${logicType} group ${i + 1}`,
            String(i),
            fieldErrors[logicType]
          );
          if (!ge.length) anyValid = true;
          allErrors.push(...ge);
        } else
          allErrors.push(
            `${logicType} group ${i + 1}: Invalid group structure`
          );
      } else {
        const v = validateCondition(c);
        if (!v.isValid) {
          v.errors.forEach((e) =>
            allErrors.push(`${logicType} condition ${i + 1}: ${e}`)
          );
          fieldErrors[logicType][String(i)] = v.fieldErrors;
        } else anyValid = true;
      }
    }
  }
  if (!Object.values(conditions).some((a) => a.length > 0))
    allErrors.push('At least one condition is required');
  else if (!anyValid)
    allErrors.push('At least one valid condition is required');
  return { isValid: !allErrors.length, errors: allErrors, fieldErrors };
};

export const getValidationMessage = (conditions: {
  [key: string]: Condition[];
}): string | null => {
  const { isValid, errors } = validateConditions(conditions);
  if (isValid) return null;
  if (errors.length === 1) return errors[0];
  return `Multiple validation errors: ${errors.slice(0, 2).join(', ')}${errors.length > 2 ? '...' : ''}`;
};
